/**
 * 文件简介：
 * - 元气大屏全链路生命周期计数器（组件 / 定时器 / 轮播 / WS / 视频 / API / 数据层）
 * - 挂载到 window.__screenLifecycleStats，与 __chartLifecycleStats 配合排查内存与重复创建
 * 使用方法：
 * - __screenLifecycleStats.summary()  打印全屏汇总（默认附带图表计数与 sleep 根组件数据快照）
 * - __screenLifecycleStats.reset()    清零（可与 __chartLifecycleStats.reset() 同时使用）
 * - __screenLifecycleStats.getStats() 原始计数对象
 * 原理：
 * - 在各模块关键路径轻量打点，不在热路径打 info 日志
 * - summary 合并 chartLifecycleStats，便于一次查看「图表 + 其它模块」是否异常增长
 */

import chartLifecycleStats from '@/utils/chartLifecycleStats';

const MAX_RECENT_EVENTS = 80;

function createEmptyStats() {
  return {
    startedAt: Date.now(),
    /** Vue 组件 mount / destroy 次数（按组件 name） */
    components: {},
    /** 定时器：创建与清理次数（用于判断泄漏：created - cleared 不应长期增大） */
    timers: {
      intervalCreated: 0,
      intervalCleared: 0,
      timeoutCreated: 0,
      timeoutCleared: 0,
      byName: {},
    },
    /** 轮播切页次数（currentPage 实际变更） */
    pageRotations: 0,
    /** WebSocket 通知通道 */
    ws: {
      connect: 0,
      disconnect: 0,
      reconnect: 0,
      reconnectFailed: 0,
      reused: 0,
    },
    /** 视频加载 */
    video: {
      loadStart: 0,
      loaded: 0,
      error: 0,
    },
    /** 气泡创建（睡眠视图） */
    bubbles: {
      created: 0,
    },
    /** 关键 API 拉取次数 */
    api: {
      fetchAppointmentData: 0,
      fetchRealtimeData: 0,
      fetchWelcomeMessage: 0,
      fetchVideoList: 0,
    },
    recentEvents: [],
  };
}

let stats = createEmptyStats();

function ensureComponentStats(name) {
  if (!stats.components[name]) {
    stats.components[name] = { mount: 0, destroy: 0 };
  }
  return stats.components[name];
}

function ensureTimerNameStats(name) {
  if (!stats.timers.byName[name]) {
    stats.timers.byName[name] = {
      intervalCreated: 0,
      intervalCleared: 0,
      timeoutCreated: 0,
      timeoutCleared: 0,
    };
  }
  return stats.timers.byName[name];
}

function pushRecentEvent(type, detail = {}) {
  stats.recentEvents.push({ at: Date.now(), type, ...detail });
  if (stats.recentEvents.length > MAX_RECENT_EVENTS) {
    stats.recentEvents.shift();
  }
}

/**
 * 从 DOM 查找 sleep 根实例，供 summary 自动附带 appointmentData / chartPool
 */
export function findSleepRootVm() {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector('.big-screen-container');
  return el && el.__vue__ ? el.__vue__ : null;
}

function countWaveDataPoints(appointmentData) {
  if (!Array.isArray(appointmentData)) {
    return { appointmentDataLength: 0, waveDataTotalPoints: 0 };
  }
  let waveDataTotalPoints = 0;
  appointmentData.forEach((item) => {
    if (!item || !item.waveData) return;
    ['delta', 'theta', 'alpha', 'beta', 'gamma'].forEach((band) => {
      const arr = item.waveData[band];
      if (Array.isArray(arr)) waveDataTotalPoints += arr.length;
    });
  });
  return {
    appointmentDataLength: appointmentData.length,
    waveDataTotalPoints,
  };
}

export const screenLifecycleStats = {
  /** 记录组件 mounted */
  recordComponentMount(componentName) {
    const name = componentName || 'Anonymous';
    ensureComponentStats(name).mount += 1;
    pushRecentEvent('component.mount', { componentName: name });
  },

  /** 记录组件 beforeDestroy */
  recordComponentDestroy(componentName) {
    const name = componentName || 'Anonymous';
    ensureComponentStats(name).destroy += 1;
    pushRecentEvent('component.destroy', { componentName: name });
  },

  /**
   * 记录定时器创建
   * @param {'interval'|'timeout'} type
   * @param {string} name 定时器语义名，如 chartUpdateTimer
   */
  recordTimerCreate(type, name = 'unknown') {
    const bucket = ensureTimerNameStats(name);
    if (type === 'interval') {
      stats.timers.intervalCreated += 1;
      bucket.intervalCreated += 1;
    } else {
      stats.timers.timeoutCreated += 1;
      bucket.timeoutCreated += 1;
    }
    pushRecentEvent('timer.create', { type, name });
  },

  recordTimerClear(type, name = 'unknown') {
    const bucket = ensureTimerNameStats(name);
    if (type === 'interval') {
      stats.timers.intervalCleared += 1;
      bucket.intervalCleared += 1;
    } else {
      stats.timers.timeoutCleared += 1;
      bucket.timeoutCleared += 1;
    }
    pushRecentEvent('timer.clear', { type, name });
  },

  recordPageRotation(fromPage, toPage) {
    stats.pageRotations += 1;
    pushRecentEvent('page.rotation', { fromPage, toPage });
  },

  recordWsConnect(meta = {}) {
    stats.ws.connect += 1;
    if (meta.reused) stats.ws.reused += 1;
    pushRecentEvent('ws.connect', meta);
  },

  recordWsDisconnect(meta = {}) {
    stats.ws.disconnect += 1;
    pushRecentEvent('ws.disconnect', meta);
  },

  recordWsReconnect(meta = {}) {
    stats.ws.reconnect += 1;
    pushRecentEvent('ws.reconnect', meta);
  },

  recordWsReconnectFailed(meta = {}) {
    stats.ws.reconnectFailed += 1;
    pushRecentEvent('ws.reconnect_failed', meta);
  },

  recordVideoLoadStart(meta = {}) {
    stats.video.loadStart += 1;
    pushRecentEvent('video.load_start', meta);
  },

  recordVideoLoaded(meta = {}) {
    stats.video.loaded += 1;
    pushRecentEvent('video.loaded', meta);
  },

  recordVideoError(meta = {}) {
    stats.video.error += 1;
    pushRecentEvent('video.error', meta);
  },

  recordBubbleCreated(meta = {}) {
    stats.bubbles.created += 1;
    pushRecentEvent('bubble.created', meta);
  },

  recordApiCall(apiName) {
    if (stats.api[apiName] != null) {
      stats.api[apiName] += 1;
      pushRecentEvent('api.call', { apiName });
    }
  },

  /**
   * 全屏汇总：合并图表计数、数据层、定时器净增等
   * @param {object} context 可选 { appointmentData, activeChartInstances }
   */
  summary(context = {}) {
    const elapsedMs = Date.now() - stats.startedAt;
    const root = findSleepRootVm();
    const appointmentData = context.appointmentData || root?.appointmentData;
    const activeChartInstances =
      context.activeChartInstances ??
      (root?.chartPool?.instances ? Object.keys(root.chartPool.instances).length : null);

    const waveStats = countWaveDataPoints(appointmentData);
    const chartRaw = chartLifecycleStats.getStats();
    const timerNet = {
      intervalNet: stats.timers.intervalCreated - stats.timers.intervalCleared,
      timeoutNet: stats.timers.timeoutCreated - stats.timers.timeoutCleared,
    };

    const result = {
      elapsedMinutes: Number((elapsedMs / 60000).toFixed(1)),
      components: { ...stats.components },
      timers: { ...stats.timers, net: timerNet },
      pageRotations: stats.pageRotations,
      ws: { ...stats.ws },
      video: { ...stats.video },
      bubbles: { ...stats.bubbles },
      api: { ...stats.api },
      data: waveStats,
      activeChartInstances,
      charts: {
        echartsInit: chartRaw.echartsInit,
        echartsInitByReason: { ...chartRaw.echartsInitByReason },
        initChartsCalls: chartRaw.initChartsCalls,
        reuseUpdate: chartRaw.reuseUpdate,
        setOptionUpdates: chartRaw.setOptionUpdates,
        dispose: chartRaw.dispose,
        disposeByReason: { ...chartRaw.disposeByReason },
      },
    };

    // eslint-disable-next-line no-console
    console.info('[ScreenLifecycle] summary', result);
    // eslint-disable-next-line no-console
    console.info('[ScreenLifecycle] recentEvents (last %d)', stats.recentEvents.length, [...stats.recentEvents]);

    return result;
  },

  reset() {
    stats = createEmptyStats();
    // eslint-disable-next-line no-console
    console.info('[ScreenLifecycle] counters reset');
    return stats;
  },

  getStats() {
    return stats;
  },
};

if (typeof window !== 'undefined') {
  window.__screenLifecycleStats = screenLifecycleStats;
}

export default screenLifecycleStats;
